import { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Empty,
  message,
  Typography,
  Space,
  Tag,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import request from '@/utils/request';
import { formatCents, formatDate } from '@/utils/format';
import { thumb } from '@/utils/image';

interface BundleRow {
  id: number;
  title: string;
  status: 'draft' | 'pending_review' | 'published' | 'rejected' | 'offline';
  coverImageId: number | null;
  priceCoins: number | null;
  priceRmbCents: number | null;
  heat: number;
  salesCount: number;
  totalEarningsCents?: number;
  publishedAt: string | null;
}

const STATUS_LABEL: Record<BundleRow['status'], { text: string; color: string }> = {
  draft: { text: '草稿', color: 'default' },
  pending_review: { text: '审核中', color: 'processing' },
  published: { text: '已发布', color: 'success' },
  rejected: { text: '已驳回', color: 'error' },
  offline: { text: '已下架', color: 'warning' },
};

const BundleListPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<BundleRow[]>([]);
  const [urlMap, setUrlMap] = useState<Map<number, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await request.get<{ items: BundleRow[] }>('/creator/bundles');
      const data = (res.data as unknown as { items: BundleRow[] }).items;
      setItems(data);
      const ids = data.map((d) => d.coverImageId).filter((v): v is number => v != null);
      if (ids.length > 0) {
        const imgRes = await request.get<{ items: { id: number; accessUrl: string }[] }>(
          `/creator/images?ids=${ids.join(',')}`,
        );
        const map = new Map<number, string>();
        for (const im of (imgRes.data as unknown as { items: { id: number; accessUrl: string }[] }).items) {
          map.set(im.id, im.accessUrl);
        }
        setUrlMap(map);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      // 不传 title: 服务端默认 "未命名套图", 创作者进入编辑页再改标题.
      const res = await request.post<{ id: number }>('/creator/bundles', {});
      const data = res.data as unknown as { id: number };
      navigate(`/bundles/${data.id}`);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  const takeOffline = async (id: number) => {
    try {
      await request.post(`/creator/bundles/${id}/take-offline`);
      message.success('已下架');
      load();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const deleteBundle = async (id: number) => {
    try {
      await request.delete(`/creator/bundles/${id}`);
      message.success('已删除');
      load();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            组合套图
          </Typography.Title>
          <Typography.Text type="secondary">
            把实验台的成果打包成商品，审核通过后上线到小程序
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} loading={creating} onClick={onCreate}>
          新建套图
        </Button>
      </Space>

      {items.length === 0 && !loading ? (
        <Card>
          <Empty className="brand-empty" description="还没有套图，点击右上角新建">
            <Button type="primary" icon={<PlusOutlined />} loading={creating} onClick={onCreate}>
              新建套图
            </Button>
          </Empty>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {items.map((item) => {
            const label = STATUS_LABEL[item.status];
            const coverUrl = item.coverImageId ? urlMap.get(item.coverImageId) : null;
            return (
              <Col key={item.id} xs={24} sm={12} md={8} lg={6}>
                <Card
                  hoverable
                  loading={loading}
                  style={{ overflow: 'hidden' }}
                  cover={
                    <div
                      style={{
                        position: 'relative',
                        height: 160,
                        background: coverUrl
                          ? undefined
                          : 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A40 60%, #F6F3EC 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#B45309',
                        overflow: 'hidden',
                      }}
                    >
                      {coverUrl ? (
                        <img
                          src={thumb(coverUrl, 200)}
                          alt={item.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <AppstoreOutlined style={{ fontSize: 36 }} />
                      )}
                      <Tag
                        color={label.color}
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          margin: 0,
                          boxShadow: '0 1px 3px rgba(0,0,0,.12)',
                        }}
                      >
                        {label.text}
                      </Tag>
                    </div>
                  }
                  actions={[
                    <a key="edit" onClick={() => navigate(`/bundles/${item.id}`)}>
                      <EditOutlined /> 编辑
                    </a>,
                    item.status === 'published' ? (
                      <a
                        key="run"
                        onClick={() => navigate(`/usage/new?bundleId=${item.id}`)}
                        style={{ color: '#D97706', fontWeight: 500 }}
                      >
                        <ThunderboltOutlined /> 运行
                      </a>
                    ) : (
                      <span key="run-dis" style={{ color: '#9CA3AF' }}>
                        <ThunderboltOutlined /> 运行
                      </span>
                    ),
                    item.status === 'published' ? (
                      <Popconfirm
                        key="offline"
                        title="确定下架？"
                        onConfirm={() => takeOffline(item.id)}
                      >
                        <a style={{ color: '#DC2626' }}>下架</a>
                      </Popconfirm>
                    ) : item.status !== 'offline' ? (
                      <Popconfirm
                        key="delete"
                        title="确定删除？"
                        onConfirm={() => deleteBundle(item.id)}
                      >
                        <a style={{ color: '#DC2626' }}>删除</a>
                      </Popconfirm>
                    ) : (
                      <span key="noop" style={{ color: '#9CA3AF' }}>—</span>
                    ),
                  ]}
                >
                  <Card.Meta
                    title={<span>{item.title}</span>}
                    description={
                      <Space direction="vertical" size={4}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          定价 {formatCents(item.priceRmbCents ?? 0)} · {item.priceCoins ?? 0} 币
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          销量 {item.salesCount} · 累计收益{' '}
                          <Typography.Text strong style={{ color: '#15803D' }}>
                            {formatCents(item.totalEarningsCents ?? 0)}
                          </Typography.Text>
                        </Typography.Text>
                        {item.publishedAt && (
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            发布于 {formatDate(item.publishedAt)}
                          </Typography.Text>
                        )}
                      </Space>
                    }
                  />
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

    </Space>
  );
};

export default BundleListPage;
